import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CustomInstructionsFormComponent,
  CustomInstructionValue,
} from '@components/forms/custom-instructions-form/custom-instructions-form.component';
import {
  UltimateGenerateRequestArticleTypeEnum,
  UltimateGenerateRequestPromptLanguageEnum,
} from '@models/Sessions/Idea/podcast-api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';

import { articleTypes } from '../../../../domain/article';

type Action = 'generate' | 'save';

interface Form {
  userTextType: FormControl<UltimateGenerateRequestArticleTypeEnum>;
}

export interface ArticleSettingsData {
  customInstruction: CustomInstructionValue;
  promptLanguage: UltimateGenerateRequestPromptLanguageEnum;
  userTextType: UltimateGenerateRequestArticleTypeEnum;
  action: Action;
  meta: {
    canGenerate: boolean;
  };
}

export interface ArticleSettingsPayload {
  customInstruction: CustomInstructionValue;
  userTextType: UltimateGenerateRequestArticleTypeEnum;
  action: Action;
}

const getUserTextTypeOptions = (
  lang: UltimateGenerateRequestPromptLanguageEnum,
) => {
  return Object.values(UltimateGenerateRequestArticleTypeEnum).map(value => ({
    label: articleTypes[value][lang],
    value,
  }));
};

@Component({
  standalone: true,
  imports: [
    SelectModule,
    ReactiveFormsModule,
    FormsModule,
    FloatLabelModule,
    ButtonModule,
    CustomInstructionsFormComponent,
    DividerModule,
  ],
  templateUrl: './article-settings-dialog-content.component.html',
  styleUrl: './article-settings-dialog-content.component.css',
})
export class ArticleSettingsComponent implements OnInit {
  form!: FormGroup<Form>;
  userTextTypeOptions = getUserTextTypeOptions('en');
  canGenerate = false;

  customInstruction: CustomInstructionValue;

  // Computed property that combines initial canGenerate with current form validity
  get isGenerateEnabled(): boolean {
    return this.canGenerate && this.form?.valid;
  }

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig<ArticleSettingsData>,
  ) {}

  ngOnInit() {
    this.createForm();
  }

  private createForm() {
    const { data } = this.dialogConfig;
    const { customInstruction, promptLanguage, userTextType, meta } = data;
    const { canGenerate } = meta;
    this.userTextTypeOptions = getUserTextTypeOptions(promptLanguage);
    this.canGenerate = canGenerate;

    this.customInstruction = customInstruction;
    this.form = this.formBuilder.group({
      userTextType:
        this.formBuilder.control<UltimateGenerateRequestArticleTypeEnum>(
          userTextType,
          [Validators.required],
        ),
    });
  }

  onCustomInstructionChange(value: CustomInstructionValue): void {
    this.customInstruction = value;
  }

  handleCancelClick(): void {
    this.dialogRef.close();
  }

  handleSaveClick(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload: ArticleSettingsPayload = {
      customInstruction: this.customInstruction,
      userTextType: this.form.get('userTextType').value,
      action: 'save',
    };

    this.dialogRef.close(payload);
  }

  handleGenerateClick(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload: ArticleSettingsPayload = {
      customInstruction: this.customInstruction,
      userTextType: this.form.get('userTextType').value,
      action: 'generate',
    };

    this.dialogRef.close(payload);
  }
}
